import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchPostBySlug, fetchPublishedPosts } from '../lib/db';
import type { Post } from '../lib/types';
import { ArrowLeft, Calendar, Loader2, ArrowRight } from 'lucide-react';
import { useSeo } from '../lib/seo';

export default function ProgramDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [related, setRelated] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useSeo({
    title: post ? `${post.title} | Kisapuk Community Initiative` : 'Program Detail | Kisapuk Community Initiative',
    description: post ? post.excerpt : 'Read more about Kisapuk Community Initiative programs, news, and community impact.',
    pathname: post ? `/programs/${post.slug}` : '/programs',
    image: post ? post.cover_image_url || 'https://your-domain.com/programs-social.png' : 'https://your-domain.com/programs-social.png',
  });

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    fetchPostBySlug(slug)
      .then((data) => {
        if (!data) {
          setNotFound(true);
        } else {
          setPost(data);
          fetchPublishedPosts(data.category)
            .then((all) => setRelated(all.filter((p) => p.id !== data.id).slice(0, 3)))
            .catch(() => {});
        }
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 px-4">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold text-stone-900">Post Not Found</h1>
          <p className="mt-4 text-stone-600">The article you're looking for doesn't exist or has been removed.</p>
          <Link to="/programs" className="btn-primary mt-6">
            <ArrowLeft className="h-4 w-4" />
            Back to Programs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
        {post.cover_image_url && (
          <img src={post.cover_image_url} alt={post.title} className="h-full w-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-950/90 via-brand-900/60 to-brand-900/30" />
        <div className="absolute bottom-0 left-0 right-0 pb-12">
          <div className="container-prose">
            <Link to="/programs" className="inline-flex items-center gap-2 text-sm text-brand-100 hover:text-gold-400 transition-colors mb-4">
              <ArrowLeft className="h-4 w-4" />
              All Programs
            </Link>
            <span className="badge-gold capitalize mb-4">{post.category}</span>
            <h1 className="font-display text-3xl font-bold text-white sm:text-4xl lg:text-5xl max-w-3xl">
              {post.title}
            </h1>
            <div className="mt-4 flex items-center gap-2 text-sm text-brand-100">
              <Calendar className="h-4 w-4" />
              {new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="container-prose">
          <div className="mx-auto max-w-3xl">
            <p className="text-lg text-stone-600 leading-relaxed font-medium border-l-4 border-gold-500 pl-6 mb-8">
              {post.excerpt}
            </p>
            <div
              className="prose-content text-stone-700 leading-relaxed [&_p]:mb-4 [&_p]:text-base"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 bg-stone-100">
          <div className="container-prose">
            <h2 className="font-display text-2xl font-bold text-stone-900 mb-8">Related Stories</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.id} to={`/programs/${r.slug}`} className="card overflow-hidden group hover:shadow-lg transition-all">
                  <div className="relative h-40 overflow-hidden bg-stone-200">
                    {r.cover_image_url && (
                      <img src={r.cover_image_url} alt={r.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-base font-semibold text-stone-900 group-hover:text-brand-700 transition-colors line-clamp-2">{r.title}</h3>
                    <p className="mt-2 text-sm text-brand-600 flex items-center gap-1">
                      Read more <ArrowRight className="h-3 w-3" />
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
